import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ArrowLeft, Handshake, Settings, RefreshCw, Link2Off, Table2, Info } from "lucide-react";

function asArray(d) {
  if (Array.isArray(d)) return d;
  if (Array.isArray(d?.items)) return d.items;
  if (Array.isArray(d?.products)) return d.products;
  if (Array.isArray(d?.prodotti)) return d.prodotti;
  if (Array.isArray(d?.data)) return d.data;
  return null;
}

function massimaliFrom(config) {
  // Raccoglie ricorsivamente tutti gli array "massimali" presenti nella config tariffaria.
  const out = [];
  const walk = (o) => {
    if (!o || typeof o !== "object") return;
    if (Array.isArray(o)) { o.forEach(walk); return; }
    Object.entries(o).forEach(([k, v]) => {
      if (k === "massimali" && Array.isArray(v)) out.push(...v);
      else walk(v);
    });
  };
  walk(config);
  return Array.from(new Set(out));
}

export default function PortalePartnerBridge() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [cfg, setCfg] = useState({ enabled: false, base_url: "", auth_header: "Authorization", auth_prefix: "Bearer ", path_prodotti: "/api/integration/products", path_tariffe: "/api/integration/tariffs/{key}", path_tariffe_all: "/api/integration/tariffs", path_documento: "", path_quote: "/api/integration/quote/{key}", quote_enabled: false, token: "" });
  const [cfgMeta, setCfgMeta] = useState({});
  const [saving, setSaving] = useState(false);
  const [tariff, setTariff] = useState(null); // {prodotto, loading, data, error, no_tariff}

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/portale/prodotti"); setRes(data); }
    catch { setRes({ configured: false }); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openSettings = async () => {
    try { const { data } = await api.get("/portale/config"); setCfgMeta(data); setCfg({ ...data, token: "" }); } catch { /* noop */ }
    setOpen(true);
  };
  const saveCfg = async () => {
    setSaving(true);
    try { await api.put("/portale/config", cfg); toast.success("Collegamento salvato"); setOpen(false); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const openTariff = async (code, name) => {
    setTariff({ prodotto: name || code, loading: true });
    try {
      const { data } = await api.get(`/portale/tariffe`, { params: { prodotto: code } });
      setTariff({ prodotto: name || code, loading: false, ...data });
    } catch (e) {
      setTariff({ prodotto: name || code, loading: false, error: formatApiErrorDetail(e.response?.data?.detail) });
    }
  };

  const products = asArray(res?.data);

  const renderProducts = () => {
    if (!products) {
      return <pre className="rounded-xl border border-slate-200 bg-white p-4 text-xs text-slate-700 overflow-x-auto" data-testid="portale-data-json">{JSON.stringify(res?.data, null, 2)}</pre>;
    }
    return (
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white" data-testid="portale-products-table">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500">
            <tr>
              <th className="text-left px-4 py-3 font-medium">Prodotto</th>
              <th className="text-left px-4 py-3 font-medium">Codice</th>
              <th className="text-left px-4 py-3 font-medium">Motore</th>
              <th className="text-left px-4 py-3 font-medium">Versione</th>
              <th className="text-left px-4 py-3 font-medium">Stato</th>
              <th className="text-right px-4 py-3 font-medium">Tariffa</th>
            </tr>
          </thead>
          <tbody>
            {products.map((p, i) => {
              const code = p.code || p.id || "";
              const attivo = p.attivo ?? p.active ?? true;
              return (
                <tr key={code || i} className="border-t border-slate-100" data-testid={`portale-prod-${code}`}>
                  <td className="px-4 py-3 text-slate-800 font-medium">{p.name || p.nome || code}</td>
                  <td className="px-4 py-3 text-slate-500 font-mono text-xs">{code}</td>
                  <td className="px-4 py-3 text-slate-500 font-mono text-xs">{p.engine_ref || "—"}</td>
                  <td className="px-4 py-3 text-slate-500">{p.versione || p.product_version || "—"}</td>
                  <td className="px-4 py-3">{attivo ? <Badge className="bg-emerald-100 text-emerald-700 border-0">Attivo</Badge> : <Badge className="bg-slate-200 text-slate-600 border-0">Non attivo</Badge>}</td>
                  <td className="px-4 py-3 text-right">
                    <Button size="sm" variant="outline" onClick={() => openTariff(code, p.name || p.nome)} data-testid={`portale-tariffa-${code}`}>
                      <Table2 className="h-3.5 w-3.5 mr-1.5" /> Struttura
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const renderTariffBody = () => {
    if (tariff?.loading) return <div className="h-24 rounded-lg bg-slate-100 animate-pulse" />;
    if (tariff?.error) return <p className="text-sm text-red-600">{tariff.error}</p>;
    if (tariff?.no_tariff) return <p className="text-sm text-slate-600">Prodotto informativo: nessuna tariffa disponibile via API.</p>;
    const config = tariff?.data?.config || tariff?.data;
    const mas = massimaliFrom(config);
    return (
      <div className="space-y-4">
        <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 p-3" data-testid="portale-tariff-disclaimer">
          <Info className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
          <p className="text-xs text-amber-800">Questa è la <b>struttura tariffaria</b> (massimali, parametri, premi minimi), non un premio calcolato. Il premio puntuale viene calcolato dai motori del Portale — disponibile con la Fase C2.</p>
        </div>
        {config?.product_name && <p className="text-sm text-slate-700"><span className="text-slate-400">Prodotto:</span> <b>{config.product_name}</b>{config.product_version ? ` · v. ${config.product_version}` : ""}</p>}
        {mas.length > 0 && (
          <div>
            <p className="text-xs uppercase tracking-wide text-slate-400 mb-2">Massimali disponibili</p>
            <div className="flex flex-wrap gap-2" data-testid="portale-massimali">
              {mas.map((m) => <Badge key={m} variant="outline" className="font-mono">{Number(m).toLocaleString("it-IT")} €</Badge>)}
            </div>
          </div>
        )}
        <div>
          <p className="text-xs uppercase tracking-wide text-slate-400 mb-2">Configurazione completa</p>
          <pre className="rounded-lg border border-slate-200 bg-slate-900 text-slate-200 p-3 text-xs overflow-auto max-h-64" data-testid="portale-tariff-json">{JSON.stringify(tariff?.data, null, 2)}</pre>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <button data-testid="portale-back-btn" onClick={() => navigate("/direzionale")} className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800 transition-colors mb-6">
          <ArrowLeft className="h-4 w-4" /> Torna a Direzionale
        </button>

        <div className="mb-8 flex items-end justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-2"><Handshake className="h-4 w-4 text-orange-500" /><p className="text-xs uppercase tracking-[0.2em] text-orange-500 font-semibold">Portale Partner</p></div>
            <h1 className="font-heading text-3xl sm:text-4xl font-medium text-slate-900 tracking-tight">Prodotti &amp; Tariffe Portale</h1>
            <p className="text-slate-500 mt-2">Vista in sola lettura collegata al Portale Partner.</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={load} disabled={loading} data-testid="portale-refresh-btn"><RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Aggiorna</Button>
            {isAdmin && <Button onClick={openSettings} data-testid="portale-settings-btn" className="brand-gradient text-white border-0"><Settings className="h-4 w-4 mr-2" /> Configura collegamento</Button>}
          </div>
        </div>

        {loading ? (
          <div className="h-40 rounded-xl bg-white border border-slate-200 animate-pulse" />
        ) : !res?.configured ? (
          <div data-testid="portale-not-configured" className="text-center py-20 rounded-2xl border border-dashed border-slate-300 bg-white">
            <div className="h-14 w-14 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4"><Link2Off className="h-6 w-6 text-amber-600" /></div>
            <h3 className="font-heading text-xl text-slate-900 mb-1">Collegamento non ancora attivo</h3>
            <p className="text-slate-500 max-w-md mx-auto">Il bridge verso il Portale Partner non è configurato. Quando il Portale esporrà l'API dei prodotti/tariffe, {isAdmin ? "inserisci URL e token con \"Configura collegamento\"." : "l'amministratore potrà attivarlo."}</p>
          </div>
        ) : res?.error ? (
          <div data-testid="portale-error" className="rounded-xl border border-red-200 bg-red-50 p-5 text-red-700">
            <p className="font-medium mb-1">Errore nel contattare il Portale</p>
            <p className="text-sm break-all">{res.error}</p>
          </div>
        ) : (
          <div data-testid="portale-connected">{renderProducts()}</div>
        )}

        {/* Dialog configurazione */}
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-heading">Configura collegamento Portale</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div className="flex items-center gap-3"><Switch checked={!!cfg.enabled} onCheckedChange={(v) => setCfg((c) => ({ ...c, enabled: v }))} data-testid="portale-enabled" /><Label className="text-sm">Collegamento attivo</Label></div>
              <div className="space-y-2"><Label>URL base del Portale</Label><Input data-testid="portale-baseurl" value={cfg.base_url} onChange={(e) => setCfg((c) => ({ ...c, base_url: e.target.value }))} placeholder="https://www.portalepartner.it" /></div>
              <div className="space-y-2"><Label>Path prodotti</Label><Input data-testid="portale-path-prodotti" value={cfg.path_prodotti} onChange={(e) => setCfg((c) => ({ ...c, path_prodotti: e.target.value }))} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Path tariffa (singola)</Label><Input data-testid="portale-path-tariffe" value={cfg.path_tariffe} onChange={(e) => setCfg((c) => ({ ...c, path_tariffe: e.target.value }))} placeholder="/api/integration/tariffs/{key}" /></div>
                <div className="space-y-2"><Label>Path tariffe (tutte)</Label><Input data-testid="portale-path-tariffe-all" value={cfg.path_tariffe_all} onChange={(e) => setCfg((c) => ({ ...c, path_tariffe_all: e.target.value }))} placeholder="/api/integration/tariffs" /></div>
              </div>
              <div className="space-y-2"><Label>Path download documenti prodotto (CGA/DIP)</Label><Input data-testid="portale-path-documento" value={cfg.path_documento || ""} onChange={(e) => setCfg((c) => ({ ...c, path_documento: e.target.value }))} placeholder="es. /api/integration/products/{code}/documents/{file_id} — vuoto se non disponibile" /></div>
              <div className="rounded-lg border border-slate-200 p-3 space-y-2">
                <div className="flex items-center gap-3"><Switch checked={!!cfg.quote_enabled} onCheckedChange={(v) => setCfg((c) => ({ ...c, quote_enabled: v }))} data-testid="portale-quote-enabled" /><Label className="text-sm">Calcolo premio attivo (C2)</Label></div>
                <Input data-testid="portale-path-quote" value={cfg.path_quote || ""} onChange={(e) => setCfg((c) => ({ ...c, path_quote: e.target.value }))} placeholder="/api/integration/quote/{key}" />
                <p className="text-[11px] text-slate-400">Accendilo quando il Portale avrà attivato il calcolo premio: il premio stimato comparirà nel dossier Business Intelligence.</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Header auth</Label><Input data-testid="portale-auth-header" value={cfg.auth_header} onChange={(e) => setCfg((c) => ({ ...c, auth_header: e.target.value }))} /></div>
                <div className="space-y-2"><Label>Prefisso</Label><Input data-testid="portale-auth-prefix" value={cfg.auth_prefix} onChange={(e) => setCfg((c) => ({ ...c, auth_prefix: e.target.value }))} /></div>
              </div>
              <div className="space-y-2"><Label>Token / chiave</Label><Input type="password" data-testid="portale-token" value={cfg.token} onChange={(e) => setCfg((c) => ({ ...c, token: e.target.value }))} placeholder={cfgMeta?.token_set ? "Token impostato — lascia vuoto per non cambiarlo" : "Token del Portale"} /></div>
              <p className="text-xs text-slate-400">Il path tariffa singola usa <code>{"{key}"}</code> come segnaposto del prodotto. Il token non viene mai mostrato. Vista in sola lettura.</p>
            </div>
            <DialogFooter><Button data-testid="portale-save-cfg" onClick={saveCfg} disabled={saving} className="brand-gradient text-white border-0">{saving ? "Salvataggio…" : "Salva"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog tariffa */}
        <Dialog open={!!tariff} onOpenChange={(v) => !v && setTariff(null)}>
          <DialogContent className="max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-heading">Struttura tariffaria — {tariff?.prodotto}</DialogTitle></DialogHeader>
            <div className="py-2">{renderTariffBody()}</div>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
